import React from 'react'
import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
  Typography
} from '@mui/material'
import { BuildingIconButton, type State, UsStateSelect, useUsStateLuts } from 'components'
import { type Building, type UsAddress } from 'services'
import { applyBuildingInfo } from 'helpers'

interface AddressSlotEditorProps {
  label: string
  slot: UsAddress
  disabled: boolean
  onSlotChange: (slot: UsAddress) => void
  buildings: Building[]
  buildingLoading: boolean
  mailingChecked: boolean
  onMailingToggle: () => void
}

/**
 * Controlled editor for a single address slot (primary or secondary): the label
 * + building picker + mailing toggle header and the address field grid. Owns no
 * edit/save/API state — the consumer (committee address block, exec org address
 * editor) wires `onSlotChange`/`onMailingToggle` to its own state and supplies
 * the building pool (filtered or full).
 */
export const AddressSlotEditor: React.FC<AddressSlotEditorProps> = ({
  label,
  slot,
  disabled,
  onSlotChange,
  buildings,
  buildingLoading,
  mailingChecked,
  onMailingToggle
}) => {
  const handleField = (field: keyof UsAddress, value: string | State | null): void => {
    onSlotChange({ ...slot, [field]: value })
  }

  const { lutValues: stateLut, loading: stateLoading } = useUsStateLuts()

  return (
    <Box>
      <Box display='flex' justifyContent='space-between' alignItems='center' pb={2}>
        <Box display='flex' alignItems='center' gap={1}>
          <Typography variant='body2' color='secondary.dark' sx={{ fontWeight: 'bold' }}>{`${label}:`}</Typography>
          <BuildingIconButton
            buildings={buildings}
            onBuildingSelect={(building) => { onSlotChange(applyBuildingInfo(building, slot, stateLut.states)) }}
            disabled={disabled}
            loading={buildingLoading || stateLoading}
          />
        </Box>
        <FormControlLabel
          control={<Switch disabled={disabled} checked={mailingChecked} onChange={onMailingToggle} />}
          label='Mailing Address'
        />
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField label='Building' value={slot.building} disabled={disabled} fullWidth
            onChange={(e) => { handleField('building', e.target.value) }} />
        </Grid>
        <Grid item xs={6}>
          <TextField label='Room' value={slot.room} disabled={disabled} fullWidth
            onChange={(e) => { handleField('room', e.target.value) }} />
        </Grid>
        <Grid item xs={6}>
          <TextField label='Address 1' value={slot.addressOne} disabled={disabled} fullWidth
            onChange={(e) => { handleField('addressOne', e.target.value) }} />
        </Grid>
        <Grid item xs={6}>
          <TextField label='Address 2' value={slot.addressTwo ?? ''} disabled={disabled} fullWidth
            onChange={(e) => { handleField('addressTwo', e.target.value) }} />
        </Grid>
        <Grid item xs={5}>
          <TextField label='City' value={slot.city} disabled={disabled} fullWidth
            onChange={(e) => { handleField('city', e.target.value) }} />
        </Grid>
        <Grid item xs={4}>
          <FormControl variant='outlined' fullWidth>
            <UsStateSelect disabled={disabled} value={slot.state}
              updateState={(e) => { handleField('state', e) }} />
          </FormControl>
        </Grid>
        <Grid item xs={3}>
          <TextField label='Postal Code' value={slot.zipCode} disabled={disabled} fullWidth
            onChange={(e) => { handleField('zipCode', e.target.value) }} />
        </Grid>
      </Grid>
    </Box>
  )
}
